// Powers the homepage hero slider (sections/hero-slider.liquid). The slide
// track is a native horizontally-scrollable flex container (overflow-x-auto
// + scroll-snap), so touch swipe already moves it on mobile with no JS at
// all. This script adds what native scrolling doesn't give for free:
//  - mouse click-and-drag scrolling for desktop (overflow-x-auto only
//    supports drag-to-scroll via touch, not via a mouse, so without this
//    desktop users could only change slides by clicking a dot)
//  - a `scroll` listener that keeps the dot indicators/current index in
//    sync with ANY scroll source (touch swipe, the mouse-drag below,
//    trackpad/wheel) - previously only dot clicks and the autoplay timer
//    ever updated `current`, so a manual swipe silently desynced the dots
//  - autoplay that pauses while hovering/dragging and resumes after,
//    instead of an unconditional interval that fights manual interaction
// Always loaded whenever there is more than one slide (regardless of the
// "Autoplay slides" setting), since dots and drag/swipe must work even with
// autoplay off - matches the dots' own `section.blocks.size > 1` condition.
(function () {
  var DRAG_THRESHOLD = 6; // px of mouse movement before a click is treated as a drag, not a tap

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initSlider(root) {
    if (root.dataset.heroSliderInitialized === 'true') return;

    var track = root.querySelector('[data-hero-slider-track]');
    var dots = root.querySelectorAll('[data-hero-slider-dot]');
    if (!track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (!slides.length) return;

    root.dataset.heroSliderInitialized = 'true';

    var speed = 5000;
    var speedAttr = root.dataset.autoplaySpeed;
    if (speedAttr) speed = parseInt(speedAttr, 10) * 1000;
    var autoplayAllowed = root.dataset.autoplay !== 'false' && slides.length > 1;

    var current = 0;
    var timer = null;
    var hovering = false;
    var dragging = false;
    var syncingFromScroll = false;

    function updateDots() {
      dots.forEach(function (dot, i) {
        var active = i === current;
        dot.classList.toggle('bg-primary', active);
        dot.classList.toggle('w-6', active);
        dot.classList.toggle('md:w-8', active);
        dot.classList.toggle('bg-transparent', !active);
      });
    }

    function goTo(index, behavior) {
      current = (index + slides.length) % slides.length;
      syncingFromScroll = true;
      track.scrollTo({ left: slides[current].offsetLeft, behavior: behavior || 'smooth' });
      updateDots();
      window.setTimeout(function () { syncingFromScroll = false; }, 500);
    }

    function canAutoplay() {
      return autoplayAllowed
        && !prefersReducedMotion()
        && !document.hidden
        && !hovering
        && !dragging;
    }

    function stopTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startTimer() {
      stopTimer();
      if (!canAutoplay()) return;
      timer = setInterval(function () {
        if (!canAutoplay()) return;
        goTo(current + 1);
      }, speed);
    }

    function resetTimer() {
      stopTimer();
      startTimer();
    }

    function manualGoTo(index) {
      goTo(index);
      resetTimer();
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        manualGoTo(i);
      });
    });

    // Keeps `current`/dots in sync with any scroll source (native touch
    // swipe, the mouse-drag below, trackpad/wheel) - not just dot clicks.
    var scrollDebounce;
    track.addEventListener('scroll', function () {
      if (syncingFromScroll) return;
      window.clearTimeout(scrollDebounce);
      scrollDebounce = window.setTimeout(function () {
        var trackWidth = track.clientWidth || 1;
        var nearest = Math.round(track.scrollLeft / trackWidth);
        nearest = Math.max(0, Math.min(slides.length - 1, nearest));
        if (nearest !== current) {
          current = nearest;
          updateDots();
        }
        resetTimer();
      }, 120);
    }, { passive: true });

    root.addEventListener('mouseenter', function () {
      hovering = true;
      resetTimer();
    });
    root.addEventListener('mouseleave', function () {
      hovering = false;
      resetTimer();
    });

    // Mouse click-and-drag scrolling. Touch pointers are ignored here since
    // touch already scrolls the track natively via overflow-x-auto - this
    // exists purely to give desktop mouse users the same left/right drag.
    var pointerId = null;
    var dragStartX = 0;
    var dragScrollStart = 0;
    var dragMoved = false;

    if (slides.length > 1) {
      track.style.cursor = 'grab';
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || slides.length < 2) return;
      dragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragScrollStart = track.scrollLeft;
      pointerId = e.pointerId;
      track.style.cursor = 'grabbing';
      stopTimer();
      if (track.setPointerCapture) {
        try { track.setPointerCapture(pointerId); } catch (err) { /* noop */ }
      }
    });

    track.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - dragStartX;
      if (Math.abs(dx) > DRAG_THRESHOLD) dragMoved = true;
      track.scrollLeft = dragScrollStart - dx;
    });

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      track.style.cursor = 'grab';
      resetTimer();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Suppress the click a real mouse-drag over a slide link would otherwise fire
    track.addEventListener('click', function (e) {
      if (dragMoved) {
        e.preventDefault();
        e.stopPropagation();
        dragMoved = false;
      }
    }, true);

    // Prevent the browser's native image-drag ghost from fighting our drag
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopTimer();
      } else {
        resetTimer();
      }
    });

    var reducedMotionQuery = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotionQuery && reducedMotionQuery.addEventListener) {
      reducedMotionQuery.addEventListener('change', resetTimer);
    }

    if (dots.length) updateDots();
    startTimer();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-hero-slider]').forEach(initSlider);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });
  if (document.readyState !== 'loading') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
